import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  ButtonV2,
  LabraToolTipV2,
  SelectV2,
  TextInputV2,
} from '@labrav/react-components'
import { get, isNil } from 'lodash'
import { SelectChangeEvent, Typography } from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { useStyles } from './ProductFreeTrialDimensions.styles'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import {
  ContractDimension,
  FlyoutProductAddEditSectionName,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import { getRandom } from '../../../common/utils/helperFunctions'
import { MultilineTruncate } from '../../../common/components/TruncateText/MultilineTruncate'

interface ProductFreeTrialDimensionsProps {
  productDimensions?: ContractDimension[]
}

export const ProductFreeTrialDimensions = ({
  productDimensions,
}: ProductFreeTrialDimensionsProps) => {
  const { classes } = useStyles()
  const formikProps = useFormikContext() as FormikProps<FlyoutProductSections>

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formikProps
  const dimensions =
    values?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS].freeTrial
      ?.dimensions || []
  const dimensionsLength = dimensions.length

  const freeTrialTouched =
    touched?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial
  const freeTrialError =
    errors?.[FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]?.freeTrial

  const randomKey = useMemo(() => getRandom(), [dimensions.length])
  const emptyObjectDimensions = {
    name: '',
    productDimensionId: '',
    label: '',
    labelDescription: '',
  }

  const selectedDimensionNames = dimensions.map(dimension => dimension.name)
  const dimensionsOptions = useMemo(() => {
    return (productDimensions || []).map(dimension => ({
      label: dimension.label,
      value: dimension.name,
    }))
  }, [productDimensions, dimensions])

  const handleChangeSelect = useCallback(
    (event: SelectChangeEvent, index: number) => {
      const selectedLabel = event.target.value as string
      const { id, label, labelDescription, name } =
        (productDimensions || []).find(
          dimension => dimension.name === selectedLabel
        ) || {}

      if (selectedDimensionNames) {
        const updatedDimension = {
          productDimensionId: id,
          quantity: '',
          label,
          labelDescription,
          name,
        }
        setFieldValue(
          `productDimensions.freeTrial.dimensions.${index}`,
          updatedDimension
        )
        setFieldTouched(
          `productDimensions.freeTrial.dimensions.${index}`,
          false
        )
      }
    },
    [productDimensions]
  )

  useEffect(() => {
    if ((dimensions || []).length === 0) {
      setFieldValue('productDimensions.freeTrial.dimensions', [
        emptyObjectDimensions,
      ])
    }
  }, [dimensions])

  return (
    <>
      <Typography className={classes.heading}>Product dimensions</Typography>
      <Typography className={classes.value}>
        Choose the dimensions & quantities you want to offer under free trial.
      </Typography>
      <div className={clsx(classes.root, classes.elementsGap)}>
        <div className={classes.titleWraper}>
          <Typography className={clsx(classes.sectionText, classes.columnName)}>
            Name
          </Typography>
          <Typography className={clsx(classes.sectionText, classes.columnDesc)}>
            Description
          </Typography>
          <Typography
            className={clsx(classes.sectionText, classes.columnPrice)}
          >
            Quantity
          </Typography>
          <div style={{ width: '2%' }}></div>
        </div>
        <div className={classes.border}></div>
        <FieldArray name="productDimensions.freeTrial.dimensions">
          {({ push, remove }) => (
            <>
              {dimensions.map((dimension, index) => {
                return (
                  <>
                    <div
                      className={classes.resourceRow}
                      data-testid="dimensions-row"
                    >
                      <div className={classes.resourcesContainer}>
                        <div
                          className={classes.columnName}
                          key={`dimensions-${randomKey}-paymentDate`}
                        >
                          <SelectV2
                            field={{
                              title: 'Select a dimension',
                              placeHolder: 'Select a dimension',
                              humanReadableKey: 'dimension',
                              id: `productDimensions.freeTrial.dimensions.${[
                                index,
                              ]}.label`,
                              isRequired: false,
                              isReadOnly: false,
                              isCustom: false,
                              defaultValue: '',
                              dataType: 'enum' as DataType,
                              options: dimensionsOptions.filter(
                                dimension =>
                                  !selectedDimensionNames
                                    .filter(id => id !== dimensions[index].name)
                                    .includes(dimension.value)
                              ),
                            }}
                            value={dimensions[index].name || ''}
                            onChange={e => handleChangeSelect(e, index)}
                            onBlur={handleBlur}
                            isError={
                              !!(
                                get(
                                  freeTrialTouched,
                                  `dimensions.${[index]}.label`,
                                  false
                                ) &&
                                get(
                                  freeTrialError,
                                  `dimensions.${[index]}.label`,
                                  false
                                )
                              )
                            }
                            errorText={
                              (!!get(
                                freeTrialTouched,
                                `dimensions.${[index]}.label`,
                                false
                              ) &&
                                get(
                                  freeTrialError,
                                  `dimensions.${[index]}.label`,
                                  ''
                                )) ||
                              ''
                            }
                          />
                        </div>
                        <div
                          className={clsx(
                            classes.alignDescription,
                            classes.descHeightWidth,
                            classes.wordWrap
                          )}
                          key={`productDimensions.freeTrial.dimensions-${randomKey}-desc`}
                          data-testid="description-select"
                        >
                          <LabraToolTipV2
                            tooltipPlacement={'right-end'}
                            description={
                              dimensions[index].labelDescription || '-'
                            }
                          >
                            <MultilineTruncate
                              text={dimensions[index].labelDescription || '-'}
                              maxLength={200}
                            />
                          </LabraToolTipV2>
                        </div>

                        <div
                          key={`productDimensions.freeTrial.dimensions-${randomKey}-quantity`}
                          className={classes.columnPrice}
                        >
                          <TextInputV2
                            keyName={`productDimensions.freeTrial.dimensions.${[
                              index,
                            ]}.quantity`}
                            title="Quantity"
                            type="number"
                            placeHolder=""
                            value={
                              !isNil(dimensions[index]?.quantity)
                                ? (dimensions[index]?.quantity as number)
                                : ''
                            }
                            updatedValue={
                              !isNil(dimensions[index]?.quantity)
                                ? (dimensions[index]?.quantity as number)
                                : ''
                            }
                            isError={
                              !!(
                                get(
                                  freeTrialTouched,
                                  `dimensions.${[index]}.quantity`,
                                  false
                                ) &&
                                get(
                                  freeTrialError,
                                  `dimensions.${[index]}.quantity`,
                                  false
                                )
                              )
                            }
                            errorText={
                              (!!get(
                                freeTrialTouched,
                                `dimensions.${[index]}.quantity`,
                                false
                              ) &&
                                get(
                                  freeTrialError,
                                  `dimensions.${[index]}.quantity`,
                                  ''
                                )) ||
                              ''
                            }
                            onChange={handleChange}
                            isReadOnly={dimensions[index].name ? false : true}
                            isRequired={false}
                            showSpinner={false}
                            onBlur={handleBlur}
                            keyToPrevent={['+', '-', 'e']}
                          />
                        </div>
                      </div>
                      {dimensionsLength > 1 ? (
                        <div className={classes.trashIcon}>
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ width: '18px', height: '20px' }}
                            data-testid="trash-dimensions"
                            onClick={() => remove(index)}
                          />
                        </div>
                      ) : (
                        <div style={{ width: '2%' }}></div>
                      )}
                    </div>
                    <div className={classes.border}></div>
                  </>
                )
              })}
              {productDimensions?.length &&
              dimensionsLength <= (productDimensions || []).length - 1 ? (
                <div>
                  <ButtonV2
                    className={classes.widthButton}
                    styleType="link"
                    onClick={() => {
                      push(emptyObjectDimensions)
                    }}
                    data-testid="add-a-dimensions-button"
                  >
                    Add more dimensions
                  </ButtonV2>
                </div>
              ) : (
                <div
                  className={classes.disabledText}
                  data-testid="add-a-dimensions-button"
                >
                  <LabraToolTipV2
                    tooltipPlacement="bottom"
                    description={
                      'All existing dimensions have already been added.'
                    }
                  >
                    Add more dimensions
                  </LabraToolTipV2>
                </div>
              )}
            </>
          )}
        </FieldArray>
      </div>
    </>
  )
}
